import React, { useState, useEffect } from "react";
import * as Icons from "lucide-react";
import { ExternalLink, Check, AlertCircle } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { UI, t } from "@/i18n/translations";
import { DEMARCHES } from "@/data/content";
import { api } from "@/lib/api";

const ALL_IDS = DEMARCHES.flatMap((th) => th.items.map((i) => i.id));

export default function Demarches() {
  const { lang } = useLang();
  const D = UI.demarches;
  const [checked, setChecked] = useState([]);

  useEffect(() => {
    api.get("/demarches/progress").then(({ data }) => setChecked(data.checked || [])).catch(() => {});
  }, []);

  const toggle = async (id) => {
    const done = !checked.includes(id);
    setChecked((prev) => (done ? [...prev, id] : prev.filter((x) => x !== id)));
    try {
      const { data } = await api.put("/demarches/progress", { item_id: id, done });
      setChecked(data.checked || []);
    } catch {
      setChecked((prev) => (done ? prev.filter((x) => x !== id) : [...prev, id]));
    }
  };

  const total = ALL_IDS.length;
  const doneCount = checked.filter((id) => ALL_IDS.includes(id)).length;
  const pct = Math.round((doneCount / total) * 100);

  return (
    <div className="animate-fade-up space-y-5">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">{t(D.title, lang)}</h1>
        <p className="text-sm text-ink/60 mt-1">{t(D.intro, lang)}</p>
      </div>

      {/* Progress */}
      <div className="rounded-[18px] bg-ink text-paper p-5 shadow-sm">
        <div className="flex items-end justify-between mb-2">
          <span className="font-display text-2xl font-extrabold">{doneCount}/{total}</span>
          <span className="text-sm text-paper/70">{t(D.progress, lang)}</span>
        </div>
        <div className="h-2.5 rounded-full bg-paper/15 overflow-hidden">
          <div
            data-testid="progress-bar"
            className="h-full bg-sage rounded-full transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {DEMARCHES.map((theme, ti) => {
        const ThemeIcon = Icons[theme.icon] || Icons.ListChecks;
        return (
          <div key={ti} data-testid={`theme-${ti}`}>
            <div className="flex items-center gap-2 mb-2.5">
              <span className="grid place-items-center w-8 h-8 rounded-xl bg-amber/15">
                <ThemeIcon className="w-4.5 h-4.5 text-amber" strokeWidth={2.2} />
              </span>
              <h2 className="font-display text-xl font-bold">{t(theme.theme, lang)}</h2>
            </div>
            <div className="space-y-2.5">
              {theme.items.map((item) => {
                const isDone = checked.includes(item.id);
                return (
                  <div
                    key={item.id}
                    className={`rounded-[18px] border p-4 transition-colors ${isDone ? "bg-sage/8 border-sage/40" : "bg-white border-line"}`}
                  >
                    <div className="flex gap-3">
                      <button
                        data-testid={`check-${item.id}`}
                        onClick={() => toggle(item.id)}
                        role="checkbox"
                        aria-checked={isDone}
                        className={`shrink-0 mt-0.5 grid place-items-center w-6 h-6 rounded-lg border-2 transition-all ${
                          isDone ? "bg-sage border-sage" : "border-line bg-paper hover:border-sage"
                        }`}
                        aria-label="toggle"
                      >
                        {isDone && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                      </button>
                      <div className="min-w-0 flex-1">
                        <h3 className={`font-semibold text-[15px] leading-snug ${isDone ? "line-through text-ink/50" : ""}`}>
                          {t(item.title, lang)}
                        </h3>
                        <p className="text-[13px] text-ink/60 leading-relaxed mt-1.5">
                          <span className="font-semibold text-ink/75">{t(D.why, lang)} : </span>
                          {t(item.why, lang)}
                        </p>
                        {item.link && (
                          <a
                            href={item.link}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 mt-2.5 text-[13px] font-semibold text-sage hover:underline"
                          >
                            {t(D.openLink, lang)}
                            {item.linkLabel ? ` · ${item.linkLabel}` : ""}
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <p className="flex items-start gap-2 text-xs text-ink/50 leading-relaxed pt-1">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber" />
        {t(D.footer, lang)}
      </p>
    </div>
  );
}
